import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import InternalSidebar from '../../components/InternalSidebar';
import InternalTopMenu from '../../components/InternalTopMenu';
import EmailVerificationBanner from '../../components/EmailVerificationBanner';
import MaskedValue from '../../components/MaskedValue';
import posthog from 'posthog-js';

const MIN_WITHDRAW_BRL = 5;
const FEE_WITHDRAW_BRL = 3.0;
const MIN_WITHDRAW_BTC = 0.00005;
const NETWORK_FEE_BTC = 0.00001;

const MAINNET_PATTERNS = {
  P2PKH: /^[1][a-km-zA-HJ-NP-Z1-9]{25,34}$/,
  P2SH: /^[3][a-km-zA-HJ-NP-Z1-9]{25,34}$/,
  BECH32_SEGWIT: /^bc1[qp][023456789acdefghjklmnpqrstuvwxyz]{37,87}$/,
  BECH32_TAPROOT: /^bc1p[023456789acdefghjklmnpqrstuvwxyz]{58}$/,
};

const TESTNET_PATTERNS = {
  P2PKH: /^[mn][a-km-zA-HJ-NP-Z1-9]{25,34}$/,
  P2SH: /^[2][a-km-zA-HJ-NP-Z1-9]{25,34}$/,
  BECH32_SEGWIT: /^tb1[qp][023456789acdefghjklmnpqrstuvwxyz]{37,87}$/,
  BECH32_TAPROOT: /^tb1p[023456789acdefghjklmnpqrstuvwxyz]{58}$/,
};

const getNetworkType = () => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname.includes('localhost')) {
      return 'testnet';
    }
  }
  
  if (typeof process !== 'undefined' && process.env) {
    if (process.env.REACT_APP_NETWORK_TYPE) {
      return process.env.REACT_APP_NETWORK_TYPE === 'mainnet' ? 'mainnet' : 'testnet';
    }
    if (process.env.NODE_ENV) {
      return process.env.NODE_ENV === 'production' ? 'mainnet' : 'testnet';
    }
  }
  
  return 'mainnet';
};

const validateBitcoinAddress = (address, network = getNetworkType()) => {
  if (!address) return false;
  
  const trimmed = address.trim();
  const patterns = network === 'mainnet' ? MAINNET_PATTERNS : TESTNET_PATTERNS;
  
  return Object.values(patterns).some(pattern => pattern.test(trimmed));
};

const getAddressTypeDescription = (network = getNetworkType()) => {
  if (network === 'mainnet') {
    return 'Legacy (1...), P2SH (3...), SegWit (bc1q...), ou Taproot (bc1p...)';
  }
  return 'Testnet: Legacy (m.../n...), P2SH (2...), SegWit (tb1q...), ou Taproot (tb1p...)';
};

const currencyFormatter = new Intl.NumberFormat('pt-BR', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const bitcoinFormatter = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 8,
  maximumFractionDigits: 8,
});

const formatCurrency = (value) => currencyFormatter.format(Number(value) || 0);
const formatBitcoin = (value) => bitcoinFormatter.format(Number(value) || 0);

const parseCurrencyInput = (value) => {
  if (!value) return NaN;
  const numeric = Number(String(value).replace(/\./g, '').replace(',', '.').replace(/[^\d.-]/g, ''));
  return Number.isFinite(numeric) ? Number(numeric.toFixed(2)) : NaN;
};

const parseBitcoinInput = (value) => {
  if (!value) return NaN;
  const digitsOnly = String(value).replace(/\D/g, '');
  if (!digitsOnly) return NaN;
  const numeric = Number(digitsOnly) / 1e8;
  return Number.isFinite(numeric) ? Number(numeric.toFixed(8)) : NaN;
};

const sanitizeBitcoinAddressInput = (value) => {
  if (!value) return '';
  return String(value)
    .replace(/[^0123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjklmnpqrstuvwxyz]/g, '')
    .slice(0, 90);
};

const isValidBitcoinAddress = (value) => {
  if (!value) return false;
  return validateBitcoinAddress(value.trim());
};

const Withdraw = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [loadingBalances, setLoadingBalances] = useState(true);
  const [balancesError, setBalancesError] = useState('');
  const [overview, setOverview] = useState(null);

  const [method, setMethod] = useState('brl'); // brl | btc
  const [stage, setStage] = useState('form'); // form | preview | success
  const isEmailVerified = Number(user?.validEmail) === 1;
  const registrationStep = Number(user?.userRegistrationStep ?? user?.registrationStep ?? 0);
  const isLevelOneOrAbove = registrationStep >= 1;

  const [amountBRL, setAmountBRL] = useState('');
  const [amountBTC, setAmountBTC] = useState('');

  const [preview, setPreview] = useState(null);
  const [globalError, setGlobalError] = useState('');

  const [copyMessage, setCopyMessage] = useState('');
  const [btcAddress, setBtcAddress] = useState('');
  const [btcAddressError, setBtcAddressError] = useState('');

  const [successInfo, setSuccessInfo] = useState(null);
  const [hasPixKey, setHasPixKey] = useState(true);

  useEffect(() => {
    if (!user?.id) return;

    const fetchOverview = async () => {
      try {
        setLoadingBalances(true);
        setBalancesError('');
        const response = await api.get(`/dashboard/overview/${user.id}`);
        setOverview(response.data || {});
      } catch (error) {
        setBalancesError(
          error.response?.data?.message ||
            error.message ||
            'Não foi possível carregar seus saldos. Tente novamente mais tarde.'
        );
      } finally {
        setLoadingBalances(false);
      }
    };

    const fetchPixKeys = async () => {
      try {
        const response = await api.get(`/bank/pix/${user.id}`);
        const keys = response.data?.keys;
        if (Array.isArray(keys)) {
          setHasPixKey(keys.length > 0);
        } else {
          const list = Array.isArray(response.data) ? response.data : [];
          setHasPixKey(list.length > 0);
        }
      } catch (error) {
        setHasPixKey(true);
      }
    };

    fetchOverview();
    fetchPixKeys();
  }, [user?.id]);

  const availableBalances = useMemo(() => {
    const rawBalances = overview?.balances?.raw || {};
    return {
      brl: Number(rawBalances.real ?? rawBalances.Real ?? 0),
      btc:
        Number(rawBalances.bitcoinAmount ?? rawBalances.BitcoinAmount ?? 0) ||
        Number(overview?.bitcoinBalance?.raw ?? overview?.bitcoinBalance ?? 0),
    };
  }, [overview]);

  const numericAmount = useMemo(() => {
    if (method === 'brl') {
      return parseCurrencyInput(amountBRL);
    }
    return parseBitcoinInput(amountBTC);
  }, [amountBRL, amountBTC, method]);

  const amountValidations = useMemo(() => {
    if (!Number.isFinite(numericAmount)) {
      return { valid: false, message: 'Informe um valor válido.' };
    }

    if (method === 'brl') {
      if (numericAmount < MIN_WITHDRAW_BRL) {
        return {
          valid: false,
          message: `O valor mínimo para saque é R$ ${formatCurrency(MIN_WITHDRAW_BRL)}.`,
        };
      }
      if (numericAmount > availableBalances.brl) {
        return {
          valid: false,
          message: 'Saldo insuficiente para este saque.',
        };
      }
      if (numericAmount <= FEE_WITHDRAW_BRL) {
        return {
          valid: false,
          message: `Informe um valor maior que a taxa (R$ ${formatCurrency(FEE_WITHDRAW_BRL)}).`,
        };
      }
      return { valid: true };
    }

    if (numericAmount < MIN_WITHDRAW_BTC) {
      return {
        valid: false,
        message: `O valor mínimo para saque é ${formatBitcoin(MIN_WITHDRAW_BTC)} BTC.`,
      };
    }

    if (numericAmount > availableBalances.btc) {
      return {
        valid: false,
        message: 'Saldo insuficiente para este saque.',
      };
    }

    return { valid: true };
  }, [numericAmount, method, availableBalances.brl, availableBalances.btc]);

  const isBtcAddressValid = useMemo(() => {
    if (!btcAddress || !btcAddress.trim()) return false;
    return isValidBitcoinAddress(btcAddress.trim());
  }, [btcAddress]);

  const handleMethodChange = (next) => {
    if (next === method) return;
    setMethod(next);
    setStage('form');
    setPreview(null);
    setSuccessInfo(null);
    setGlobalError('');
    setCopyMessage('');
    setAmountBRL('');
    setAmountBTC('');
    setBtcAddress('');
    setBtcAddressError('');
  };

  const handlePercentage = (percentage) => {
    if (loadingBalances || balancesError) return;
    if (method === 'brl') {
      const value = availableBalances.brl * percentage;
      setAmountBRL(
        currencyFormatter
          .format(value)
          .replace('R$ ', '')
          .replace('R$', '')
          .trim()
      );
    } else {
      const value = availableBalances.btc * percentage;
      setAmountBTC(bitcoinFormatter.format(value));
    }
    setGlobalError('');
  };

  const handleCopy = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopyMessage('Copiado!');
      setTimeout(() => setCopyMessage(''), 2000);
    } catch (error) {
      setCopyMessage('Não foi possível copiar.');
      setTimeout(() => setCopyMessage(''), 2000);
    }
  };

  const handlePreview = async (event) => {
    event.preventDefault();
    if (!user?.id) {
      setGlobalError('Sessão expirada. Faça login novamente.');
      return;
    }
    if (method === 'brl' && (!isEmailVerified || !isLevelOneOrAbove)) {
      setGlobalError('Saques em reais são liberados a partir do nível 1 e com email verificado.');
      return;
    }
    if (method === 'brl' && !hasPixKey) {
      setGlobalError('Cadastre uma chave Pix antes de sacar em reais.');
      return;
    }
    if (!amountValidations.valid) {
      setGlobalError(amountValidations.message);
      return;
    }

    try {
      setGlobalError('');
      setPreview(null);
      setCopyMessage('');
      const response = await api.post('/withdraw/preview', {
        userId: user.id,
        currency: method === 'brl' ? 'brl' : 'btc',
        amount: numericAmount,
      });
      setPreview(response.data || {});
      setStage('preview');
    } catch (error) {
      setGlobalError(
        error.response?.data?.message ||
          error.message ||
          'Não foi possível gerar o resumo do saque. Tente novamente.'
      );
    }
  };

  const handleConfirm = async () => {
    if (!user?.id || !preview) return;

    if (preview.currency === 'btc') {
      const trimmed = btcAddress.trim();
      if (!trimmed) {
        setBtcAddressError('Informe um endereço Bitcoin.');
        return;
      }
      if (!isValidBitcoinAddress(trimmed)) {
        const network = getNetworkType();
        setBtcAddressError(
          `Use um endereço Bitcoin válido para ${network === 'mainnet' ? 'produção' : 'testnet'}: ${getAddressTypeDescription(network)}`
        );
        return;
      }
      setBtcAddressError('');
    } else if (!isEmailVerified || !isLevelOneOrAbove) {
      setGlobalError('Saques em reais são liberados a partir do nível 1 e com email verificado.');
      return;
    }

    try {
      setGlobalError('');
      const payload = {
        userId: user.id,
        currency: preview.currency,
        amount: preview.amount?.raw ?? numericAmount,
      };

      if (preview.currency === 'btc') {
        payload.address = btcAddress.trim();
      } else {
        payload.userUuid =
          user?.uuid || user?.Uuid || user?.UUID || user?.userUuid || user?.UserUuid || null;
        payload.pixKey = preview.pixKey;
      }

      await api.post('/withdraw/confirm', payload);
      setSuccessInfo({
        currency: preview.currency,
        details: preview,
        address: preview.currency === 'btc' ? btcAddress.trim() : null,
      });
      setStage('success');

      posthog.capture('withdrawal_confirmed', {
        currency: preview.currency,
        amount: preview.amount?.raw ?? numericAmount,
      });
    } catch (error) {
      setGlobalError(
        error.response?.data?.message ||
          error.message ||
          'Não foi possível concluir o saque. Tente novamente.'
      );
    }
  };

  const renderForm = () => (
    <form className="space-y-6" onSubmit={handlePreview}>
      <div className="flex flex-wrap gap-3">
        <button
          type="button"
          onClick={() => handleMethodChange('brl')}
          className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
            method === 'brl'
              ? 'bg-slate-900 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <i className="bi-cash-stack"></i> R$ Real
        </button>
        <button
          type="button"
          onClick={() => handleMethodChange('btc')}
          className={`flex items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold transition-colors ${
            method === 'btc'
              ? 'bg-slate-900 text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          <i className="bi-currency-bitcoin"></i> Bitcoin
        </button>
      </div>

      <div>
        {method === 'brl' ? (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-600">Valor do saque</label>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                {[0.25, 0.5, 0.75, 1].map((pct) => (
                  <button
                    key={pct}
                    type="button"
                    onClick={() => handlePercentage(pct)}
                    className="rounded-full border border-slate-200 px-3 py-1 font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    {pct * 100}%
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-[#f3f3f3] px-4 py-3 text-sm font-medium text-slate-700 focus-within:border-slate-400">
              <span className="text-slate-400">R$</span>
              <input
                type="text"
                inputMode="decimal"
                placeholder="0,00"
                className="w-full bg-transparent outline-none placeholder:text-slate-400"
                value={amountBRL}
                onChange={(event) => {
                  const rawDigits = event.target.value.replace(/\D/g, '');
                  const numeric = Number(rawDigits) / 100;
                  if (Number.isNaN(numeric)) {
                    setAmountBRL('');
                    return;
                  }
                  setAmountBRL(
                    currencyFormatter
                      .format(numeric)
                      .replace('R$ ', '')
                      .replace('R$', '')
                      .trim()
                  );
                }}
                onFocus={(event) => event.target.select()}
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Mínimo: R$ {formatCurrency(MIN_WITHDRAW_BRL)} • Taxa: R$ {formatCurrency(FEE_WITHDRAW_BRL)}
            </p>
          </div>
        ) : (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="text-sm font-medium text-slate-600">Valor do saque</label>
              <div className="flex items-center gap-2 text-xs text-slate-400">
                {[0.25, 0.5, 0.75, 1].map((pct) => (
                  <button
                    key={pct}
                    type="button"
                    onClick={() => handlePercentage(pct)}
                    className="rounded-full border border-slate-200 px-3 py-1 font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
                  >
                    {pct * 100}%
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-[#f3f3f3] px-4 py-3 text-sm font-medium text-slate-700 focus-within:border-slate-400">
              <span className="text-slate-400">BTC</span>
              <input
                type="text"
                inputMode="numeric"
                placeholder="0.00000000"
                className="w-full bg-transparent outline-none placeholder:text-slate-400"
                value={amountBTC}
                onChange={(event) => {
                  const digits = event.target.value.replace(/\D/g, '');
                  if (!digits) {
                    setAmountBTC('');
                    return;
                  }
                  const numeric = Number(digits) / 1e8;
                    setAmountBTC(bitcoinFormatter.format(numeric));
                }}
                onFocus={(event) => event.target.select()}
              />
            </div>
            <p className="mt-2 text-xs text-slate-500">
              Mínimo: {formatBitcoin(MIN_WITHDRAW_BTC)} BTC • Taxa da rede: {formatBitcoin(NETWORK_FEE_BTC)} BTC
            </p>
          </div>
        )}
      </div>

      {globalError ? (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{globalError}</div>
      ) : null}

      {method === 'brl' && !hasPixKey ? (
        <button
          type="button"
          onClick={() => navigate('/bank/pix/add')}
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-tBlue px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-tBlue2"
        >
          <i className="bi-plus-circle"></i> Cadastrar chave PIX
        </button>
      ) : (
        <button
          type="submit"
          className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-tBlue px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-tBlue2 disabled:opacity-60"
          disabled={
            !amountValidations.valid ||
            !Number.isFinite(numericAmount) ||
            (method === 'brl' && (!isEmailVerified || !isLevelOneOrAbove))
          }
        >
          {method === 'brl' ? (
            <>
              <i className="bi-arrow-left-right"></i> Continuar com saque em Reais
            </>
          ) : (
            <>
              <i className="bi-arrow-left-right"></i> Continuar com saque em Bitcoin
            </>
          )}
        </button>
      )}
    </form>
  );

  const renderPreviewBRL = () => {
    const formatBRL = (value) => {
      if (!value) return 'R$ 0,00';
      const formatted = typeof value === 'string' ? value : formatCurrency(value);
      return formatted.startsWith('R$') ? formatted : `R$ ${formatted}`;
    };

    return (
      <div className="space-y-6">
        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-6 text-sm text-slate-600">
          <h2 className="text-base font-semibold text-slate-800 mb-4">Confirme os dados do saque</h2>
          <dl className="space-y-3">
            <div className="flex items-center justify-between">
              <dt>Valor do saque</dt>
              <dd className="font-semibold text-slate-900">{formatBRL(preview?.amount?.formatted || preview?.amount?.raw)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt>Taxa de processamento</dt>
              <dd className="font-semibold text-slate-900">{formatBRL(preview?.fee?.formatted || preview?.fee?.raw)}</dd>
            </div>
            <div className="flex items-center justify-between">
              <dt>Total que será creditado</dt>
              <dd className="text-base font-semibold text-emerald-600">{formatBRL(preview?.total?.formatted || preview?.total?.raw)}</dd>
            </div>
          </dl>
        </div>

      <div className="rounded-2xl border border-slate-200 bg-white px-6 py-6 text-sm text-slate-600">
        <h3 className="text-base font-semibold text-slate-800 mb-4">Conta cadastrada</h3>
        {preview?.pixKey ? (
          <div className="flex items-center justify-between">
            <span>Chave Pix</span>
            <span className="font-semibold text-slate-900 break-all">{preview.pixKey}</span>
          </div>
        ) : (
          <p className="text-sm text-slate-500">
            Nenhuma chave Pix encontrada. Cadastre uma conta em{' '}
            <a href="/bank/pix" className="font-semibold text-tBlue underline">
              Chaves Pix
            </a>{' '}
            para continuar.
          </p>
        )}
      </div>

      {globalError ? (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{globalError}</div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => {
            setStage('form');
            setPreview(null);
            setCopyMessage('');
          }}
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <i className="bi-arrow-left"></i> Fazer outro saque
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          className="inline-flex items-center gap-2 rounded-full bg-[#0255F1] px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-[#0144CC]"
          disabled={!preview?.pixKey}
        >
          <i className="bi-check-circle"></i> Confirmar saque
        </button>
      </div>
    </div>
    );
  };

  const renderPreviewBTC = () => (
    <div className="space-y-6">
      <div className="rounded-2xl border border-slate-200 bg-white px-6 py-6 text-sm text-slate-600">
        <h2 className="text-base font-semibold text-slate-800 mb-4">Confirme os dados do saque</h2>
        <dl className="space-y-3">
          <div className="flex items-center justify-between">
            <dt>Valor do saque</dt>
            <dd className="font-semibold text-slate-900">
              {(() => {
                const value = preview?.amount?.formatted || '0,00000000';
                return value.endsWith('BTC') ? value : `${value} BTC`;
              })()}
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt>Taxa Stackfy (1%)</dt>
            <dd className="font-semibold text-slate-900">
              {(() => {
                const value = preview?.stackfyFee?.formatted || '0,00000000';
                return value.endsWith('BTC') ? value : `${value} BTC`;
              })()}
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt>Taxa da rede</dt>
            <dd className="font-semibold text-slate-900">
              {(() => {
                const value = preview?.networkFee?.formatted || '0,00000000';
                return value.endsWith('BTC') ? value : `${value} BTC`;
              })()}
            </dd>
          </div>
          <div className="flex items-center justify-between">
            <dt>BTC que você recebe</dt>
            <dd className="text-base font-semibold text-emerald-600">
              {(() => {
                const value = preview?.total?.formatted || '0,00000000';
                return value.endsWith('BTC') ? value : `${value} BTC`;
              })()}
            </dd>
          </div>
        </dl>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium text-slate-600">Endereço Bitcoin de destino</label>
        <input
          type="text"
          placeholder={getNetworkType() === 'mainnet' ? 'Endereço BTC (1..., 3..., bc1...)' : 'Endereço BTC Testnet (m..., n..., 2..., tb1...)'}
          className="w-full rounded-2xl border-2 border-slate-400 bg-[#f3f3f3] px-4 py-3 text-sm text-slate-700 outline-none focus:border-slate-600 focus:bg-[#f3f3f3] hover:bg-[#f3f3f3]"
          style={{ backgroundColor: '#f3f3f3' }}
          value={btcAddress}
          maxLength={90}
          onChange={(event) => {
            const sanitized = sanitizeBitcoinAddressInput(event.target.value);
            setBtcAddress(sanitized);
            setBtcAddressError('');
          }}
        />
        <p className="text-xs text-slate-500">
          Verifique atentamente o endereço. Saques em Bitcoin não podem ser revertidos após confirmados.
        </p>
        {btcAddressError ? (
          <p className="text-xs font-semibold text-red-600">{btcAddressError}</p>
        ) : null}
      </div>

      {globalError ? (
        <div className="rounded border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">{globalError}</div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => {
            setStage('form');
            setPreview(null);
            setBtcAddress('');
            setBtcAddressError('');
          }}
          className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
        >
          <i className="bi-arrow-left"></i> Fazer outro saque
        </button>
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!isBtcAddressValid}
          className={`inline-flex items-center gap-2 rounded-full px-6 py-3 text-sm font-semibold text-white transition-colors ${
            !isBtcAddressValid
              ? 'bg-slate-300 cursor-not-allowed'
              : 'bg-[#0255F1] hover:bg-[#0144CC]'
          }`}
        >
          <i className="bi-check-circle"></i> Confirmar saque
        </button>
      </div>
    </div>
  );

  const renderSuccess = () => {
    if (!successInfo) {
      return null;
    }

    const isBRL = successInfo.currency === 'brl';
    const details = successInfo.details || {};

    const formatBRL = (value) => {
      if (!value) return 'R$ 0,00';
      const formatted = typeof value === 'string' ? value : formatCurrency(value);
      return formatted.startsWith('R$') ? formatted : `R$ ${formatted}`;
    };

    return (
      <div className="space-y-6 text-sm text-slate-600">
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-6 py-6">
          <h2 className="text-base font-semibold text-emerald-700 mb-2">Solicitação enviada com sucesso!</h2>
          <p className="text-sm">
            {isBRL
              ? 'Seu saque em Reais foi enviado para processamento e pode levar até 2 dias úteis para ser concluído.'
              : 'Seu saque em Bitcoin foi solicitado. Aguarde a confirmação da rede para visualizar o crédito na carteira de destino.'}
          </p>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white px-6 py-6">
          <h3 className="text-base font-semibold text-slate-800 mb-4">Resumo do saque</h3>
          <dl className="space-y-3">
            <div className="flex items-center justify-between">
              <dt>Valor solicitado</dt>
              <dd className="font-semibold text-slate-900">
                {isBRL ? (
                  formatBRL(details?.amount?.formatted || details?.amount?.raw)
                ) : (
                  (() => {
                    const value = details?.amount?.formatted || '0,00000000';
                    return value.endsWith('BTC') ? value : `${value} BTC`;
                  })()
                )}
              </dd>
            </div>
            {isBRL ? (
              <>
                <div className="flex items-center justify-between">
                  <dt>Taxa de processamento</dt>
                  <dd className="font-semibold text-slate-900">{formatBRL(details?.fee?.formatted || details?.fee?.raw)}</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt>Valor líquido</dt>
                  <dd className="text-base font-semibold text-emerald-600">{formatBRL(details?.total?.formatted || details?.total?.raw)}</dd>
                </div>
              </>
            ) : (
              <>
                <div className="flex items-center justify-between">
                  <dt>Taxa Stackfy</dt>
                  <dd className="font-semibold text-slate-900">
                    {(() => {
                      const value = details?.stackfyFee?.formatted || '0,00000000';
                      return value.endsWith('BTC') ? value : `${value} BTC`;
                    })()}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt>Taxa da rede</dt>
                  <dd className="font-semibold text-slate-900">
                    {(() => {
                      const value = details?.networkFee?.formatted || '0,00000000';
                      return value.endsWith('BTC') ? value : `${value} BTC`;
                    })()}
                  </dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt>BTC enviado</dt>
                  <dd className="text-base font-semibold text-emerald-600">
                    {(() => {
                      const value = details?.total?.formatted || '0,00000000';
                      return value.endsWith('BTC') ? value : `${value} BTC`;
                    })()}
                  </dd>
                </div>
                {successInfo.address ? (
                  <div className="flex flex-col">
                    <dt className="text-xs text-slate-500 uppercase tracking-wide">Destino</dt>
                    <dd className="break-all font-semibold text-slate-900">{successInfo.address}</dd>
                  </div>
                ) : null}
              </>
            )}
          </dl>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => {
              setStage('form');
              setPreview(null);
              setSuccessInfo(null);
              setAmountBRL('');
              setAmountBTC('');
              setBtcAddress('');
            }}
            className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-xs font-semibold text-slate-600 hover:bg-slate-100 transition-colors"
          >
            Realizar novo saque
          </button>
          <button
            type="button"
            onClick={() => navigate('/transactions')}
            className="inline-flex items-center gap-2 rounded-full bg-slate-900 px-5 py-2 text-xs font-semibold text-white transition-colors hover:bg-slate-700"
          >
            Ver extrato
          </button>
        </div>
      </div>
    );
  };

  const [sidebarOpen, setSidebarOpen] = useState(false);
  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  return (
    <div className="min-h-screen bg-[#f7f7f7] text-slate-700">
      <div className="min-h-screen flex flex-col lg:flex-row">
        <div className="lg:hidden">
          <div className="flex items-center justify-between px-6 py-4 bg-white shadow-sm">
            <button
              type="button"
              className="text-2xl text-slate-700"
              onClick={toggleSidebar}
              aria-label="Abrir menu"
            >
              <i className="bi-list"></i>
            </button>
            <img src="/img/branded/logob.png" alt="Stackfy" className="h-8" />
            <div className="w-7" />
          </div>
        </div>

        <InternalSidebar active="withdraw" open={sidebarOpen} onClose={() => setSidebarOpen(false)} />

        {sidebarOpen && (
          <button
            type="button"
            className="fixed inset-0 bg-slate-900/50 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
            aria-label="Fechar menu"
          ></button>
        )}

        <main className="flex-1 px-6 py-8 lg:px-10 lg:py-12">
          <EmailVerificationBanner />
          <header className="mb-8 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="text-sm font-semibold flex items-center gap-2 text-slate-600 hover:text-slate-900 transition-colors"
            >
              <i className="bi-arrow-left-short text-xl"></i> Voltar
            </button>

            <InternalTopMenu onLogout={logout} />
          </header>

          <section className="mx-auto max-w-3xl">
            {method === 'brl' && (!isEmailVerified || !isLevelOneOrAbove) ? (
              <div className="mb-6 flex flex-col gap-4 rounded-3xl border border-amber-200 bg-amber-50 px-6 py-5 text-sm text-amber-900 shadow-sm md:flex-row md:items-center md:justify-between">
                <div className="space-y-1">
                  <h2 className="text-base font-semibold text-amber-900">
                    Complete o nível 1 para sacar em reais
                  </h2>
                  <p>
                    Saques em reais exigem email verificado.
                    <br />
                    Conclua sua verificação para ativar esta funcionalidade.
                  </p>
                </div>
                <a
                  href="/account/level"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-600"
                >
                  Níveis de conta
                  <i className="bi-arrow-right-short text-lg"></i>
                </a>
              </div>
            ) : null}
            <div className="rounded-3xl border border-slate-200 bg-white px-6 py-6 shadow-sm sm:px-10 sm:py-10">
              <div className="mb-6">
                <h1 className="text-xl font-semibold text-slate-900">Sacar</h1>
                <p className="text-sm text-slate-500">
                  {stage === 'preview' 
                    ? 'Valide as informações abaixo e Confirme o Saque'
                    : 'Escolha a moeda e informe o valor que deseja sacar.'}
                </p>
              </div>

              {stage === 'form' && (
                <div className="mb-6">
                  {loadingBalances ? (
                    <p className="text-xs text-slate-500">Carregando saldos...</p>
                  ) : balancesError ? (
                    <p className="text-xs font-semibold text-red-600">{balancesError}</p>
                  ) : (
                    <div className="flex flex-wrap items-center gap-4 text-sm font-semibold text-slate-700">
                      <span className="flex items-center gap-2">
                        <img
                          src="https://static.vecteezy.com/system/resources/previews/011/419/619/original/brazil-flag-file-free-png.png"
                          alt="Real"
                          className="w-4"
                        />
                        R$ <MaskedValue>{formatCurrency(availableBalances.brl)}</MaskedValue>
                      </span>
                      <span className="flex items-center gap-2">
                        <i className="bi-currency-bitcoin text-[#F7931A] text-base" />
                        <MaskedValue>{formatBitcoin(availableBalances.btc)}</MaskedValue> BTC
                      </span>
                    </div>
                  )}
                </div>
              )}

              {stage === 'form' && renderForm()}
              {stage === 'preview' && preview
                ? preview.currency === 'brl'
                  ? renderPreviewBRL()
                  : renderPreviewBTC()
                : null}
              {stage === 'success' && renderSuccess()}
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default Withdraw;


